import React, { useEffect, useRef, useState } from 'react'
import { registerComponent } from 'react-register-dom'
import DamVideo from '@components/DamVideo'
import { cls } from '@utils/classnames'
import { getBooleanFromValue } from '@utils/getBooleanFromValue'
import { isMobile, useWindowResize } from '@utils/mediaQueries'
import { prefersReducedMotion } from '@utils/prefersReducedMotion'

import VideoCover from './VideoCover'

type StringOrBoolean = string | boolean

export type HeaderTag = 'h2' | 'h3' | 'h4'

export interface Link {
  url: string
  externalizedURL: string
  mappedURL: string
  htmlAttributes: Record<string, unknown>
  valid: boolean
}

export interface VideoCaptions {
  link: Link
  label: string
  languageCode: string
  role: string
}
interface VideoProperties {
  fromDam: StringOrBoolean
  id: string
  headline: string
  headlineHeadingLevel: HeaderTag
  title: string
  titleHeadingLevel: HeaderTag
  description: string
  videoUrl: string
  videoFromDamUrl: string
  thumbnailUri: string
  thumbnailAlt: string
  autoplay: string
  isEditMode: StringOrBoolean
  loopVideo: string
  hideVideoControls: string
  decorativeVideo: string
  verticalVideoUrl?: string
  shouldLazyLoad?: StringOrBoolean
  fetchPriorityHigh?: boolean
  videoCaptionsJson?: string
  playButtonAriaLabel?: string
}

const Video: React.FC<VideoProperties> = ({
  fromDam: isFromDam = 'false',
  id,
  headline,
  headlineHeadingLevel: HeadlineTag = 'h2',
  title,
  titleHeadingLevel: TitleTag = 'h3',
  description,
  videoUrl,
  videoFromDamUrl,
  thumbnailUri,
  thumbnailAlt,
  isEditMode = 'false',
  autoplay = 'false',
  loopVideo = 'false',
  hideVideoControls = 'false',
  verticalVideoUrl,
  decorativeVideo = 'false',
  shouldLazyLoad,
  fetchPriorityHigh,
  videoCaptionsJson,
  playButtonAriaLabel = 'Play',
}: // eslint-disable-next-line sonarjs/cognitive-complexity
VideoProperties): JSX.Element => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(autoplay == 'true' && !prefersReducedMotion())
  const videoReference = useRef<HTMLVideoElement>(null)
  const videoWrapperReference = useRef<HTMLDivElement>(null)
  const videoParameters = videoUrl ? new URLSearchParams(videoUrl.split('?')[1]) : null
  const isEditModeBoolean = getBooleanFromValue(isEditMode)
  const isFromDamBoolean = getBooleanFromValue(isFromDam)
  const shouldNotLoadVideo = Boolean(prefersReducedMotion() && decorativeVideo === 'true')
  const canLoadVideoFromDam = Boolean(isFromDamBoolean && videoFromDamUrl && !shouldNotLoadVideo)
  const canLoadVideoFromSocialMedia = Boolean(!isFromDamBoolean && videoUrl && !shouldNotLoadVideo)
  const isMobileScreen = useWindowResize(isMobile)

  let videoCaptions: VideoCaptions[] | undefined

  try {
    videoCaptions = videoCaptionsJson ? JSON.parse(videoCaptionsJson) : undefined
  } catch (error) {
    if (error instanceof Error) {
      console.error('Invalid JSON:', error.message)
    } else {
      console.error('An unknown error occurred while parsing JSON.')
    }
  }

  const shouldHideVideoControls = getBooleanFromValue(hideVideoControls) || !isVideoPlaying ? 'true' : 'false'

  useEffect(() => {
    if (isVideoPlaying) {
      videoReference.current?.play()
      videoWrapperReference.current?.removeAttribute('inert')
    } else {
      videoReference.current?.pause()
      videoWrapperReference.current?.setAttribute('inert', 'true')
    }
  }, [isVideoPlaying])

  if (!isEditModeBoolean) {
    videoParameters?.append('autoplay', '1')
  }
  videoParameters?.append('mute', '1')

  function handleVideoCoverOnClick() {
    const videoSource = isFromDamBoolean ? 'DAM' : videoUrl.split('.')[1]

    window.adobeDataLayer?.push({
      event: 'clicked video play',
      eventInfo: {
        reference: 'video player',
        playerType: videoSource,
        videoName: title,
      },
    })
    setIsVideoPlaying(true)
  }

  return (
    <div
      className={cls({
        'cmp-video': true,
        'cmp-video--has-mobile-video': verticalVideoUrl,
      })}
    >
      {headline && <HeadlineTag className="cmp-video__headline">{headline}</HeadlineTag>}
      <div className="cmp-video__container">
        <div className="cmp-video__player">
          {(!isVideoPlaying && (
            <VideoCover
              id={`video-cover-${id}`}
              src={thumbnailUri}
              altText={thumbnailAlt}
              ariaLabel={title}
              buttonAriaLabel={playButtonAriaLabel}
              onClick={handleVideoCoverOnClick}
              shouldLazyLoad={shouldLazyLoad}
              fetchPriorityHigh={fetchPriorityHigh}
              showPlayButton={decorativeVideo !== 'true'}
            ></VideoCover>
          )) ||
            null}

          {canLoadVideoFromDam && (
            <div ref={videoWrapperReference} aria-hidden={isVideoPlaying}>
              <DamVideo
                ref={videoReference}
                videoUrl={isMobileScreen && verticalVideoUrl ? verticalVideoUrl : videoFromDamUrl}
                isEditModeBoolean={isEditModeBoolean}
                loopVideo={loopVideo}
                isVideoPlaying={isVideoPlaying}
                hideVideoControls={shouldHideVideoControls}
                decorativeVideo={decorativeVideo}
                videoCaptions={videoCaptions}
              />
            </div>
          )}

          {canLoadVideoFromSocialMedia && (
            <div ref={videoWrapperReference} aria-hidden={isVideoPlaying}>
              <iframe
                id={`video-iframe-${id}`}
                title="video"
                src={`${videoUrl}?${isVideoPlaying && videoParameters ? videoParameters.toString() : ''}`}
                allow="autoplay; encrypted-media"
                className="cmp-video__iframe"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </div>
        {title || description ? (
          <div className="cmp-video__content">
            {title && <TitleTag className="cmp-video__title">{title}</TitleTag>}
            {description && <p className="cmp-video__description" dangerouslySetInnerHTML={{ __html: description }} />}
          </div>
        ) : null}
      </div>
    </div>
  )
}

registerComponent('Video', Video)

export default Video
